import { Controller } from '@hotwired/stimulus'

type FieldRule = {
  trigger: string
  action: string[]
  target: string
}

type FieldRules = Record<string, FieldRule[]>

type FormField = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement

// Maps each action to the action applied when the trigger reverses.
// `restore` is internal-only — it never appears in authored field_rules JSON;
// it is dispatched here as the inverse of `clear`.
const INVERSE_ACTION: Record<string, string> = {
  clear: 'restore',
  disable: 'enable',
  enable: 'disable',
}

/**
 * Evaluates SubmissionView `field_rules` client-side. The rules object is
 * keyed by trigger checkbox field name; when a trigger checkbox changes,
 * each rule's actions are applied to its target field wrapper, or reversed
 * when the checkbox is unchecked.
 */
export default class FieldRulesController extends Controller {
  declare readonly rulesValue: FieldRules

  static values = {
    rules: Object,
  }

  connect() {
    Object.entries(this.rulesValue).forEach(([triggerKey, rules]) => {
      const trigger = this.#findTrigger(triggerKey, 'checkbox')
      if (!trigger) return

      const handler = () => this.#applyRules(trigger, rules)
      trigger.addEventListener('change', handler)

      // apply handler on connect to sync initial state
      handler()
    })
  }

  #applyAction(target: HTMLElement, action: string) {
    switch (action) {
      case 'clear':
        this.#clearInputs(target)
        break
      case 'restore':
        this.#restoreInputs(target)
        break
      case 'disable':
        this.#setDisabled(target, true)
        break
      case 'enable':
        this.#setDisabled(target, false)
        break
    }
  }

  #applyRules(field: HTMLInputElement, rules: FieldRule[]) {
    const active = field.checked

    rules.forEach(rule => {
      const target = this.#findTarget(rule.target)
      if (!target) return

      rule.action.forEach(action => {
        const ruleAction = active ? action : INVERSE_ACTION[action]
        if (!ruleAction) return

        this.#applyAction(target, ruleAction)
      })
    })
  }

  #clearInputs(target: HTMLElement) {
    this.#fields(target).forEach(field => {
      if (field.value !== '') {
        field.dataset.fieldRulesPreviousValue = field.value
      }

      field.value = ''
    })
  }

  #fields(target: HTMLElement): NodeListOf<FormField> {
    return target.querySelectorAll<FormField>('input')
  }

  #findTrigger(fieldName: string, type: string): HTMLInputElement | null {
    return this.element.querySelector<HTMLInputElement>(`[data-field-name="${fieldName}"] input[type="${type}"]`)
  }

  #findTarget(fieldName: string): HTMLElement | null {
    return this.element.querySelector<HTMLElement>(`[data-field-name="${fieldName}"]`)
  }

  #restoreInputs(target: HTMLElement) {
    this.#fields(target).forEach(field => {
      const previous = field.dataset.fieldRulesPreviousValue
      if (previous === undefined) return

      // set the previous value to the field
      field.value = previous

      // remove the stored previous value from the dataset
      delete field.dataset.fieldRulesPreviousValue
    })
  }

  #setDisabled(target: HTMLElement, disabled: boolean) {
    this.#fields(target).forEach(field => {
      // disable the field, which prevents the field
      // from sending data on form submit
      field.disabled = disabled

      field.classList.toggle('cursor-not-allowed', disabled)
      field.classList.toggle('bg-gray-50', disabled)
    })
  }
}
